import type { AxiosError } from "axios";
import apiAfiliado from "@/api/afiliado";
import type { PerfilAfiliado } from "@/stores/afiliado";

// Chamadas da área do afiliado (B3, `/api/v1/afiliado/*`). Toda chamada leva o
// `language` da página, para as mensagens da API virem no mesmo idioma.
//
// Sessão que caiu (401) ou cadastro que deixou de valer (403 com `motivo`) limpa
// a sessão da área e volta ao login com a mensagem da API. Por isso o composable
// é chamado no setup: o router fica guardado para depois.

export type Card = { valor: number; anterior: number; variacao: number | null };

export type Semana = { inicio: string; fim: string; valor: number; futura: boolean };

export type PlanoDoPainel = {
  valor: string;
  tipo: string;
  periodo: number;
  vendas: number;
  conversao: number | null;
  comissao: number;
};

export type Painel = {
  plano: string;
  periodo: {
    mes: string;
    inicio: string;
    fim: string;
    parcial: boolean;
    comparacao: { inicio: string; fim: string };
  };
  cards: {
    acessos: Card;
    compras: Card;
    renovacoes: Card;
    vendas: Card;
    cadastros: Card;
  };
  grafico: { semanas: Semana[] };
  insight: { conversao: number; media: number; crescimento: number } | null;
  planos: PlanoDoPainel[];
  planos_disponiveis: Array<{ valor: string; tipo: string; periodo: number }>;
};

export type Comissoes = {
  proxima_comissao: { valor: number; vendas: number; ciclo_percentual: number };
  historico: Array<{
    mes: string;
    situacao: "pago" | "aberto";
    valor: number;
    vendas: number;
    pago_em: string | null;
  }>;
};

export type ModeloDaLista = { id: number; nome: string };

export type LinkDeModelo = {
  id: number;
  essay_id: number;
  nome: string;
  making_of: boolean;
  link: string;
  criado_em: string | null;
};

/** Pacote do material de apoio (B6). As datas vêm em "YYYY-MM-DD". */
export type PacoteDeMaterial = {
  id: number;
  inicio: string;
  modelo: string;
  editorial: string;
  tipo: string;
  fotos: number;
  videos: number;
  duracao_videos: number;
  atualizado_em: string | null;
};

export type Material = { destaque: PacoteDeMaterial | null; pacotes: PacoteDeMaterial[] };

export type PedidoDeMaterial = {
  id: number;
  essay_id: number | null;
  modelo: string;
  situacao: "pendente" | "atendido";
  criado_em: string | null;
};

const MOTIVOS_DE_SAIDA = ["sessao_expirada", "nao_afiliado", "em_analise", "desativado"];

export function useAfiliadoApi() {
  const route = useRoute();
  const router = useRouter();
  const store = afiliadoStore();

  const idioma = () => (route.params.language === "en" ? "en" : "pt-br");

  /** Mensagem da API, se houver. */
  function mensagemDe(erro: unknown): string {
    const resposta = (erro as AxiosError<{ message?: string }>)?.response;
    return resposta?.data?.message ?? "";
  }

  function statusDe(erro: unknown): number {
    return (erro as AxiosError)?.response?.status ?? 0;
  }

  /**
   * 401, ou 403 com motivo de sessão: limpa e volta ao login. Devolve true quando
   * tratou; o chamador só para.
   */
  function tratarSaida(erro: unknown): boolean {
    const resposta = (erro as AxiosError<{ message?: string; motivo?: string }>)?.response;
    const status = resposta?.status ?? 0;
    const motivo = resposta?.data?.motivo ?? "";

    if (status !== 401 && !(status === 403 && MOTIVOS_DE_SAIDA.includes(motivo))) {
      return false;
    }

    store.limpar(resposta?.data?.message ?? "");
    router.replace({ name: "language-afiliado", params: { language: idioma() } });
    return true;
  }

  async function login(email: string, password: string) {
    const { data } = await apiAfiliado.post<{
      authorisation: { token: string };
      afiliado: PerfilAfiliado;
    }>("v1/afiliado/login", { email, password, language: idioma() });

    store.entrar(data.authorisation.token, data.afiliado);
    return data.afiliado;
  }

  /** Sai mesmo se a API falhar: o token some do navegador de qualquer jeito. */
  async function logout() {
    try {
      await apiAfiliado.post("v1/afiliado/logout", { language: idioma() });
    } catch {
      // segue
    }
    store.limpar();
    router.replace({ name: "language-afiliado", params: { language: idioma() } });
  }

  async function me() {
    const { data } = await apiAfiliado.get<PerfilAfiliado>("v1/afiliado/me", {
      params: { language: idioma() },
    });
    store.atualizarPerfil(data);
    return data;
  }

  async function painel(mes?: string, plano?: string) {
    const { data } = await apiAfiliado.get<Painel>("v1/afiliado/painel", {
      params: { language: idioma(), mes: mes || undefined, plano: plano || undefined },
    });
    return data;
  }

  async function comissoes() {
    const { data } = await apiAfiliado.get<Comissoes>("v1/afiliado/comissoes", {
      params: { language: idioma() },
    });
    return data;
  }

  async function modelos() {
    const { data } = await apiAfiliado.get<{ modelos: ModeloDaLista[] }>("v1/afiliado/modelos", {
      params: { language: idioma() },
    });
    return data.modelos;
  }

  async function links() {
    const { data } = await apiAfiliado.get<{ links: LinkDeModelo[] }>("v1/afiliado/links", {
      params: { language: idioma() },
    });
    return data.links;
  }

  async function criarLink(essayId: number) {
    const { data } = await apiAfiliado.post<{ link: LinkDeModelo }>("v1/afiliado/links", {
      essay_id: essayId,
      language: idioma(),
    });
    return data.link;
  }

  async function material() {
    const { data } = await apiAfiliado.get<Material>("v1/afiliado/material", {
      params: { language: idioma() },
    });
    return data;
  }

  /** URL assinada (10 min) do zip do pacote, no host da API. */
  async function urlDoPacote(pacoteId: number) {
    const { data } = await apiAfiliado.post<{ url: string }>(`v1/afiliado/material/${pacoteId}/download`, {
      language: idioma(),
    });
    return data.url;
  }

  /** Modelo da lista (`essayId`) ou o nome digitado em "Outra modelo". */
  async function pedirMaterial(pedido: { essayId?: number; nome?: string }) {
    const { data } = await apiAfiliado.post<{ pedido: PedidoDeMaterial }>("v1/afiliado/material/pedidos", {
      essay_id: pedido.essayId,
      nome: pedido.nome,
      language: idioma(),
    });
    return data.pedido;
  }

  return {
    idioma,
    login,
    logout,
    me,
    painel,
    comissoes,
    modelos,
    links,
    criarLink,
    material,
    urlDoPacote,
    pedirMaterial,
    tratarSaida,
    mensagemDe,
    statusDe,
  };
}
